/**
 * Booking form — field catalogue (V2 engine).
 *
 * Each entry describes one *simple* form input — Text, Number, Textarea,
 * Select, YesNo radios, Checkbox, MultiChips. Composite cross-dependent
 * UI (cold-chain Y/N, contamination, temperatures, ConfirmCards,
 * Doc-AI, DriverPicker, etc.) lives in registry/slots.js, NOT here.
 *
 * Ordering: within a tab, items are rendered by `position` ascending.
 * Picklist categories driven by `lbs_picklist_field_layout` are merged
 * in at render time by the engine and slotted by their own
 * (target_tab, target_position) — see BookingFormShell.
 *
 * Stable contract:
 *   - `key`        → property on the form state object (matches DB column
 *                    or a JSON sub-key handled in useBookingForm)
 *   - `tab`        → default tab (Phase 2 overrides will substitute this)
 *   - `position`   → integer in [0, 1000]
 *   - `component`  → renderer key from /components/*Field.jsx
 *   - `label`      → default UI label (Phase 2 overrides will substitute)
 *   - `testid`     → preserved verbatim from the legacy tab files
 *   - `required`   → consulted by useBookingForm.missing (CREATE-only)
 *   - `visibleWhen`→ optional predicate (values) => bool, NOT applied to
 *                    fields with positional layout; used by registry to
 *                    hide e.g. tz-badge when no tz known.
 *   - `placeholder` (TextField/NumberField/Textarea), `upperCase`
 *     (TextField), `step`, `min` (NumberField) — pass-through props.
 */

export const FIELDS = [
  // ── DOCS ────────────────────────────────────────────────────────────
  { key: "po_number",       tab: "docs", position: 100, component: "TextField",
    label: "PO number",        testid: "booking-po-input" },
  { key: "invoice_number",  tab: "docs", position: 110, component: "TextField",
    label: "Invoice number" },
  { key: "manifest_number", tab: "docs", position: 120, component: "TextField",
    label: "Manifest number" },

  // ── SCHEDULE (the cross-dependent header is a slot; these are the
  //    plain trailing inputs that came after the time row) ────────────
  { key: "title",                tab: "schedule", position: 500, component: "TextField",
    label: "Title (optional)",
    testid: "booking-title-input",
    placeholder: "Short label for this booking" },
  { key: "other_party_name",     tab: "schedule", position: 510, component: "TextField",
    label: "Other party",
    testid: "booking-other-party-input",
    placeholder: "Carrier / supplier name" },
  { key: "other_party_contact",  tab: "schedule", position: 520, component: "TextField",
    label: "Other party contact",
    placeholder: "Phone or email" },
  { key: "description",          tab: "schedule", position: 530, component: "TextField",
    label: "Description" },
  { key: "external_ref",         tab: "schedule", position: 600, component: "TextField",
    label: "External ref (booker / customer)",
    testid: "booking-external-ref",
    placeholder: "Customer PO / weighbridge ticket / booker ref" },
  { key: "internal_ref",         tab: "schedule", position: 610, component: "TextField",
    label: "Internal ref / control",
    testid: "booking-internal-ref",
    placeholder: "Owner / client_admin control number" },

  // ── GOODS ───────────────────────────────────────────────────────────
  { key: "sender_name",      tab: "goods", position: 100, component: "TextField",
    label: "Sender (contact name)", testid: "booking-sender-input" },
  { key: "sender_company",   tab: "goods", position: 110, component: "TextField",
    label: "Sender company", testid: "booking-sender-company" },
  { key: "sender_phone",     tab: "goods", position: 120, component: "TextField",
    label: "Sender phone", testid: "booking-sender-phone" },
  { key: "sender_email",     tab: "goods", position: 130, component: "TextField",
    label: "Sender email", testid: "booking-sender-email", inputType: "email" },
  { key: "receiver_name",    tab: "goods", position: 140, component: "TextField",
    label: "Receiver (contact name)", testid: "booking-receiver-input" },
  { key: "receiver_company", tab: "goods", position: 150, component: "TextField",
    label: "Receiver company", testid: "booking-receiver-company" },
  { key: "goods_description", tab: "goods", position: 200, component: "TextareaField",
    label: "Goods description", rows: 2, fullWidth: true },
  { key: "quantity",         tab: "goods", position: 300, component: "NumberField",
    label: "Quantity", min: "0" },
  { key: "weight",           tab: "goods", position: 310, component: "NumberField",
    label: "Weight (kg)", min: "0", step: "0.01" },
  { key: "volume",           tab: "goods", position: 320, component: "NumberField",
    label: "Volume (m³)", min: "0", step: "0.01" },

  // ── VEHICLE (regos + mass limits; picklist vehicle_type is layout-driven) ─
  { key: "rego_prime_mover", tab: "vehicle", position: 100, component: "TextField",
    label: "Prime mover rego", testid: "booking-rego-prime-input", alwaysRequired: true,
    upperCase: true, placeholder: "e.g. AB-12-CD" },
  { key: "rego_trailer_1",   tab: "vehicle", position: 110, component: "TextField",
    label: "Trailer 1 rego", testid: "booking-rego-trailer-1-input", upperCase: true },
  { key: "rego_trailer_2",   tab: "vehicle", position: 120, component: "TextField",
    label: "Trailer 2 rego", testid: "booking-rego-trailer-2-input", upperCase: true },
  { key: "rego_trailer_3",   tab: "vehicle", position: 130, component: "TextField",
    label: "Trailer 3 rego", testid: "booking-rego-trailer-3-input", upperCase: true },
  // Mass limits get a heading via a slot below in slots.js (position 200).
  { key: "gvm_kg",        tab: "vehicle", position: 210, component: "NumberField",
    label: "GVM", min: "0", testid: "booking-gvm-input", placeholder: "42500" },
  { key: "steer_axle_kg", tab: "vehicle", position: 220, component: "NumberField",
    label: "Steer axle", min: "0" },
  { key: "drive_axle_kg", tab: "vehicle", position: 230, component: "NumberField",
    label: "Drive axle", min: "0" },
  { key: "axle_group_notes", tab: "vehicle", position: 240, component: "TextareaField",
    label: "Axle-group notes", rows: 2, fullWidth: true,
    placeholder: "e.g. Tri-axle group: 22,500 kg" },

  // ── RESTRAINT (picklist fields layout-driven; everything else here) ─
  // Restraint counts (under a "Restraint counts" heading from slots.js @ 200)
  { key: "straps_count",          tab: "restraint", position: 210, component: "NumberField",
    label: "Straps", min: "0", testid: "booking-straps-count" },
  { key: "chains_count",          tab: "restraint", position: 220, component: "NumberField",
    label: "Chains", min: "0", testid: "booking-chains-count" },
  { key: "boards_sticks_count",   tab: "restraint", position: 230, component: "NumberField",
    label: "Boards / sticks", min: "0", testid: "booking-boards-sticks-count" },
  { key: "other_restraint_type",  tab: "restraint", position: 240, component: "TextField",
    label: "Other (type)", testid: "booking-other-restraint-type",
    placeholder: "e.g. dunnage bag" },
  { key: "other_restraint_count", tab: "restraint", position: 250, component: "NumberField",
    label: "Other (count)", min: "0", testid: "booking-other-restraint-count" },
  // Curtains / Gates pill-row sits under a slot heading @ 300.
  { key: "has_curtains", tab: "restraint", position: 310, component: "CheckboxPill",
    label: "Curtains", testid: "booking-has-curtains-checkbox" },
  { key: "has_gates",    tab: "restraint", position: 320, component: "CheckboxPill",
    label: "Gates",    testid: "booking-has-gates-checkbox" },
  // Pallets block heading slot @ 400, then the two count fields.
  { key: "pallets_in",  tab: "restraint", position: 410, component: "NumberField",
    label: "Pallets IN (+)",  min: "0", testid: "booking-pallets-in" },
  { key: "pallets_out", tab: "restraint", position: 420, component: "NumberField",
    label: "Pallets OUT (−)", min: "0", testid: "booking-pallets-out" },

  // ── COMPLIANCE (cold-chain block is a slot; food/dg/notes here) ────
  { key: "food_safety", tab: "compliance", position: 300, component: "ChipsField",
    label: "Food safety attestations", testid: "booking-food-safety",
    optionsFromPicks: "food_safety", fullWidth: true },
  { key: "dangerous_goods", tab: "compliance", position: 310, component: "ChipsField",
    label: "Dangerous goods classes", testid: "booking-dg",
    optionsFromPicks: "dg_class", fullWidth: true },
  { key: "compliance_notes", tab: "compliance", position: 400, component: "TextareaField",
    label: "Compliance notes", rows: 3, fullWidth: true },
  { key: "special_requirements", tab: "compliance", position: 410, component: "TextareaField",
    label: "Special requirements", rows: 2, fullWidth: true,
    testid: "special-requirements-input",
    placeholder: "Anything driver / FLD needs to know in advance…" },
];

/** All field keys grouped by their default tab. Used by useBookingForm. */
export function fieldsForTab(tabKey, overrides = null) {
  const list = overrides || FIELDS;
  return list
    .filter((f) => f.tab === tabKey)
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}
